import React, { FC, useEffect } from "react";
import { defineMessages, useIntl } from "react-intl";
import { Button, Form, RadioButton, RadioGroup } from "@natera/form";
import { StepperContext } from "@natera/stepper";
import { DRAW_PREFERENCE_TO_SHOW } from "@app/pages/private/sampleDraw/sampleDraw";
import { HEAP_EVENTS } from "@app/provider/types";
import {
  DrawRequestContext,
  HeapAnalyticDataContext,
  TestDetailsContext,
  UppAuthContext,
} from "@app/provider";
import { StepperContent } from "@app/components/ui/layout";
import { SpinnerView } from "@app/components";
import { useDialog } from "@natera/platform/lib/hooks";
import { ClinicRequestModal } from "@app/components/sampleDraw/dialogs";
import { TestType } from "@app/provider/testData/types";
import StepperHeaderProgress from "@app/components/stepperHeaderProgress";

import "./sampleDrawPreference.scss";

const messages = defineMessages({
  clinicPreferenceTitle: {
    id: "clinicPreferenceTitle",
    defaultMessage: "My Clinic",
  },
  clinicPreferenceDescription: {
    id: "clinicPreferenceDescription",
    defaultMessage:
      "Contact your clinic directly to see if they offer on-site blood draw services.",
  },
  sampleDrawPreferenceTitle: {
    id: "sampleDrawPreferenceTitle",
    defaultMessage: "Sample Collection",
  },
  sampleDrawPreferenceDescriptionTitle: {
    id: "sampleDrawPreferenceDescriptionTitle",
    defaultMessage: "What is the best way to get a blood draw?",
  },
  sampleDrawPreferenceDescriptionContent: {
    id: "sampleDrawPreferenceDescriptionContent",
    defaultMessage:
      "Natera will cover the cost of your blood draw when you use one of the following methods. Select the method that works best for you.",
  },
  sampleDrawPreferenceMobilePhlebotomyTitle: {
    id: "sampleDrawPreferenceMobilePhlebotomyTitle",
    defaultMessage: "Mobile Blood Draw",
  },
  sampleDrawPreferenceMobilePhlebotomyDescription: {
    id: "sampleDrawPreferenceMobilePhlebotomyDescription",
    defaultMessage:
      "You can have someone come to your desired location to perform your blood draw.",
  },
  sampleDrawPreferenceLabTitle: {
    id: "sampleDrawPreferenceLabTitle",
    defaultMessage: "Local Lab",
  },
  sampleDrawPreferenceLabDescription: {
    id: "sampleDrawPreferenceLabDescription",
    defaultMessage: "Visit a Natera-approved lab to have your blood drawn.",
  },
  sampleDrawPreferenceNext: {
    id: "sampleDrawPreferenceNext",
    defaultMessage: "Next",
  },
  sampleDrawPreferenceBack: {
    id: "sampleDrawPreferenceBack",
    defaultMessage: "Back",
  },
  sampleDrawPreferenceLegend: {
    id: "sampleDrawPreferenceLegend",
    defaultMessage: "Sample collection methods",
  },
});

export const SAMPLE_DRAW_PREFERENCE_LIST = {
  clinic: {
    title: messages.clinicPreferenceTitle,
    description: messages.clinicPreferenceDescription,
  },
  lab: {
    title: messages.sampleDrawPreferenceLabTitle,
    description: messages.sampleDrawPreferenceLabDescription,
  },
  mobilePhlebotomy: {
    title: messages.sampleDrawPreferenceMobilePhlebotomyTitle,
    description: messages.sampleDrawPreferenceMobilePhlebotomyDescription,
  },
};

export interface SampleDrawPreferenceProps {
  handleSelectSampleDrawPreference: (
    draw_preference?: DRAW_PREFERENCE_TO_SHOW
  ) => void;
  onBack: () => void;
  token?: string;
}

const SampleDrawPreference: FC<SampleDrawPreferenceProps> = ({
  handleSelectSampleDrawPreference,
  onBack,
  token: tokenProp,
}) => {
  const intl = useIntl();

  const { resolve } = React.useContext(StepperContext);
  const { isLoading, verifiedSampleDrawData, sendDrawAtLabChoiceActivity } =
    React.useContext(DrawRequestContext);

  const { drawRequestDataForHeapEventData } = React.useContext(
    HeapAnalyticDataContext
  );

  const { getTestDetails } = React.useContext(TestDetailsContext);
  const { profile } = React.useContext(UppAuthContext);

  const testDetails = getTestDetails();

  const testType =
    testDetails?.testType ??
    verifiedSampleDrawData?.sampleInfo.sample?.testType;
  const clinicModal = useDialog(ClinicRequestModal);
  const isOncologyOrder =
    testDetails?.isOncologyOrder ??
    verifiedSampleDrawData?.sampleInfo.sample?.isOncologyOrder;

  const isWHTestType =
    testType &&
    [
      TestType.EMPOWER,
      TestType.HORIZON,
      TestType.PANORAMA,
      TestType.SINGLE_GENE_NIPT,
      TestType.VISTARA,
    ].includes(testType) &&
    !isOncologyOrder;

  const [selectedSampleDrawPreference, setSelectedSampleDrawPreference] =
    React.useState<DRAW_PREFERENCE_TO_SHOW | undefined>(
      isWHTestType ? undefined : DRAW_PREFERENCE_TO_SHOW.lab
    );

  useEffect(() => {
    handleSelectSampleDrawPreference(selectedSampleDrawPreference);
  }, [selectedSampleDrawPreference]);

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    const heapProps = {
      lims_clinic_id: drawRequestDataForHeapEventData?.lims_clinic_id,
      drawtype:
        selectedSampleDrawPreference ===
        DRAW_PREFERENCE_TO_SHOW.mobilePhlebotomy
          ? "mobile_phlebotomy"
          : selectedSampleDrawPreference,
      test_name: drawRequestDataForHeapEventData?.test_name,
      business_unit: drawRequestDataForHeapEventData?.business_unit,
      draw_coordinator: drawRequestDataForHeapEventData?.draw_coordinator,
      flow: profile ? "account" : "guest",
    };
    heap.track(HEAP_EVENTS.upp_sampledraw_pref, heapProps);
    if (selectedSampleDrawPreference === DRAW_PREFERENCE_TO_SHOW.clinic) {
      clinicModal.open({ goBack: clinicModal.close, isGuest: !profile });
      return;
    }
    if (selectedSampleDrawPreference === DRAW_PREFERENCE_TO_SHOW.lab) {
      const orderUidPublic =
        verifiedSampleDrawData?.sampleInfo?.sample?.orderUidPublic;
      // Don't block step completion on activity logging: resolve immediately and send in background.
      // .catch() prevents unhandled rejection if the activity request fails.
      sendDrawAtLabChoiceActivity({ token: tokenProp, orderUidPublic }).catch(
        () => {}
      );
      resolve({});
      return;
    }
    resolve({});
  };

  const handleSampleDrawPreferenceSelect = (
    drawPreferenceType: DRAW_PREFERENCE_TO_SHOW
  ) => {
    setSelectedSampleDrawPreference(drawPreferenceType);
  };

  const renderDrawPreferenceItem = (
    drawPreferenceType: DRAW_PREFERENCE_TO_SHOW
  ) => {
    const drawPreferenceText =
      drawPreferenceType in SAMPLE_DRAW_PREFERENCE_LIST &&
      SAMPLE_DRAW_PREFERENCE_LIST[drawPreferenceType];

    return drawPreferenceText ? (
      <RadioButton
        data-testid={drawPreferenceType}
        key={drawPreferenceType}
        className="sample-draw-preference-list__item"
        label={
          <>
            <div className="sample-draw-preference-list__item-title">
              {intl.formatMessage(drawPreferenceText.title)}
            </div>
            <div className="sample-draw-preference-list__item-desc">
              {intl.formatMessage(drawPreferenceText.description)}
            </div>
          </>
        }
        value={drawPreferenceType}
      />
    ) : null;
  };

  const getDrawPreferences = () =>
    Object.keys(DRAW_PREFERENCE_TO_SHOW)
      .filter((drawPreferenceType: DRAW_PREFERENCE_TO_SHOW) => {
        if (!isWHTestType)
          return drawPreferenceType !== DRAW_PREFERENCE_TO_SHOW.clinic;
        return drawPreferenceType;
      })
      .map((drawPreferenceType: DRAW_PREFERENCE_TO_SHOW) =>
        renderDrawPreferenceItem(drawPreferenceType)
      );

  return (
    <div className="sample-draw-preference">
      <SpinnerView isLoading={isLoading} />
      <StepperHeaderProgress />
      <StepperContent
        title={intl.formatMessage(messages.sampleDrawPreferenceTitle)}
        subTitle={intl.formatMessage(
          messages.sampleDrawPreferenceDescriptionTitle
        )}
        description={intl.formatMessage(
          messages.sampleDrawPreferenceDescriptionContent
        )}
      >
        <Form
          onSubmit={handleSubmit}
          noValidate
          buttons={
            <>
              <Button
                type="submit"
                unelevated
                disabled={!selectedSampleDrawPreference}
                data-testid="submit"
              >
                {intl.formatMessage(messages.sampleDrawPreferenceNext)}
              </Button>
              <Button onClick={onBack} data-testid="cancel">
                {intl.formatMessage(messages.sampleDrawPreferenceBack)}
              </Button>
            </>
          }
        >
          <div className="sample-draw-preference-list">
            <fieldset>
              <legend className="visually-hidden">
                {intl.formatMessage(messages.sampleDrawPreferenceLegend)}
              </legend>
              <RadioGroup
                name="sampleDrawPreferenceList"
                value={selectedSampleDrawPreference}
                onChange={handleSampleDrawPreferenceSelect}
              >
                {getDrawPreferences()}
              </RadioGroup>
            </fieldset>
          </div>
        </Form>
      </StepperContent>
      {clinicModal.getDialog()}
    </div>
  );
};

export default SampleDrawPreference;
